import React, { useMemo, useState } from 'react';
import css from '../SelfHostedModal.scss';
import {
  Box,
  Button,
  Divider,
  Heading,
  Loader,
  Text,
  TextButton,
} from '@wix/design-system';
import { SelfHostedModalLayout } from '../SelfHostedModalLayout';
import { useSelfHostedContext } from '../SelfHostedModalContextProvider';
import { createAppFromTemplate } from '@wix/ambassador-devcenter-apps-v1-app-template/http';
import { DataHooks } from '../../../../../constants/dataHooks';
import { useSingleTemplateData } from '../../../../../hooks/useSingleTemplateData';
import { DEPLOY_APP_LINK_DOC } from '../../../../../constants/routes';
import { Trans, useTranslation } from '@wix/wix-i18n-config';
import { useHttpClient } from '@wix/fe-essentials/react';
import { useNotifications } from '../../../../../hooks/useNotifications';
import { createHub } from '@wix/fe-essentials/error-monitor';
import * as Sentry from '@sentry/browser';
import { SENTRY_DSN } from '../../../../../constants/sentryDSN';
import { SelfHostedModalBiParams } from '../types';
import { ExternalLink } from '@wix/wix-ui-icons-common';
import { DeploymentHostType } from '@wix/ambassador-devcenter-apps-v1-app-template/types';
import { isValidUrl } from '../../../../../utils/validators';
import { BaseUrlInput } from '../components/BaseUrlInput';
import { AppNameInput } from '../components/AppNameInput';

const biParams: SelfHostedModalBiParams = {
  stepName: 'create-app-step',
  mainCTAName: 'create & continue',
  secondaryCTAName: 'back',
};

export const CreateAppStep = () => {
  const { t } = useTranslation();
  const { formData, nextStep, updateAppDetails, previousStep, onClose } =
    useSelfHostedContext();
  const { baseUrl, appName, selectedDeploymentProvider } = formData;
  const httpClient = useHttpClient();
  const isContinueDisabled = !baseUrl || !appName;
  const [isCreatingApp, setIsCreatingApp] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const errorMonitor = createHub({
    dsn: SENTRY_DSN,
    Sentry,
  });
  const { showToast } = useNotifications();
  const { template } = useSingleTemplateData();
  const urlInvalid = useMemo(() => {
    if (!baseUrl) {
      return t('app.template-page.modal.self-hosted.step2.base-url.missing');
    }
    if (!isValidUrl(baseUrl)) {
      return t('app.template-page.modal.self-hosted.step2.base-url.not-valid');
    }
    return false;
  }, [baseUrl, t]);

  const onCreate = async () => {
    setIsSubmitted(true);
    if (urlInvalid) {
      return null;
    }
    setIsCreatingApp(true);
    try {
      const res = await httpClient.request(
        createAppFromTemplate({
          templateId: template?.id || '',
          templateConfig: {
            appInfo: {
              name: appName,
            },
            baseUrl,
          },
        }),
      );
      const app = res?.data?.app;
      updateAppDetails({
        appId: app?.id || '',
        appSecret: app?.appSecrets?.appSecret || '',
        appPublicKey: app?.appSecrets?.webhookPublicKey
          ? window.btoa(app?.appSecrets?.webhookPublicKey)
          : '',
      });
      nextStep();
    } catch (e) {
      showToast({
        theme: 'error',
        message: t('app.template-page.modal.self-hosted.step2.create-error'),
      });
      errorMonitor.captureException(e);
      onClose();
    }
  };
  const selectedProviderLowerCase = selectedDeploymentProvider?.toLowerCase();

  const openDeploymentLink = () => {
    const isVercelOrNetlifySelected =
      selectedDeploymentProvider === DeploymentHostType.VERCEL ||
      selectedDeploymentProvider === DeploymentHostType.NETLIFY;

    if (isVercelOrNetlifySelected) {
      const deploymentHost = template?.deploymentHosts?.find(
        ({ deploymentHostType }) =>
          deploymentHostType === selectedDeploymentProvider,
      );
      deploymentHost?.url && window.open(deploymentHost.url, '_blank');
    } else {
      template?.repositoryUrl && window.open(template.repositoryUrl, '_blank');
    }
  };
  return (
    <SelfHostedModalLayout
      className={css.createAppStep}
      biParams={biParams}
      primaryButtonText={
        isCreatingApp ? (
          <div className={css.loadingStateButton}>
            <Loader size="tiny" />
            <span>
              {t('app.template-page.modal.self-hosted.step2.creating-your-app')}
            </span>
          </div>
        ) : (
          t('app.template-page.modal.self-hosted.step2.primary-action')
        )
      }
      primaryButtonProps={{
        disabled: isContinueDisabled,
      }}
      primaryButtonTooltipProps={{
        maxWidth: '235px',
        content: isContinueDisabled
          ? t('app.template-page.modal.self-hosted.step2.continue.disabled')
          : null,
      }}
      primaryButtonOnClick={onCreate}
      secondaryButtonText={t(
        'app.template-page.modal.self-hosted.step2.secondary-action',
      )}
      secondaryButtonOnClick={previousStep}
    >
      <Heading size="small">
        {t(
          `app.template-page.modal.self-hosted.step2.deploy-to-provider-${selectedProviderLowerCase}`,
        )}
      </Heading>
      <Text size="medium" weight="thin">
        {t(
          `app.template-page.modal.self-hosted.step2.deploy-to-provider-description-${selectedProviderLowerCase}`,
        )}
      </Text>
      <Box paddingTop="12px">
        <Button
          suffixIcon={<ExternalLink />}
          dataHook="deployment-link"
          onClick={openDeploymentLink}
        >
          {t(
            `app.template-page.modal.self-hosted.step2.deploy-to-provider-cta-${selectedProviderLowerCase}`,
          )}
        </Button>
      </Box>
      <Divider direction="horizontal" className={css.divider} />
      <Heading size="small">
        {t('app.template-page.modal.self-hosted.step2.variants-title')}
      </Heading>
      <Text size="medium" weight="thin">
        {t('app.template-page.modal.self-hosted.step2.variants-sub-title')}
      </Text>
      <Box gap="24px" paddingTop="12px">
        <BaseUrlInput isFormSubmitted={isSubmitted} />
        <AppNameInput />
      </Box>
      <Text size="small" weight="thin" className={css.needHelp}>
        <Trans
          i18nKey={`app.template-page.modal.self-hosted.step2.base-url-need-help-${selectedDeploymentProvider?.toLowerCase()}`}
        >
          <TextButton
            dataHook={DataHooks.SelfHostModalMoreInfoDoc}
            onClick={() => {
              window.open(
                selectedDeploymentProvider ===
                  DeploymentHostType.UNKNOWN_DEPLOYMENT_HOST_TYPE
                  ? `${template.repositoryUrl}#deployment-on-a-different-platform`
                  : DEPLOY_APP_LINK_DOC[selectedDeploymentProvider || ''],
              );
            }}
          />
        </Trans>
      </Text>
    </SelfHostedModalLayout>
  );
};
